import { DELETION_FLAG, SENSOR_TYPE, SensorKey, UserKey, parse } from "@packages/core";
import { useForm, useSelector } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuDownload } from "react-icons/lu";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  ButtonStyle2,
  FileUpload,
  PageName,
  PaperStyle,
  Spinner,
  TypographyStyle,
} from "#components";
import {
  BedBatchAllocationHeaderRows,
  BedBatchAllocationRowSchema,
  toBedBatchAllocationRow,
} from "#models";
import {
  $confirm,
  allocateBed,
  removeResidentInfiniteQueryCache,
  removeSensorInfiniteQueryCache,
  removeSensorsCache,
  resetBedInfiniteQueries,
  saveAsFile,
  useBedInfiniteQuery,
  useFacilityQuery,
  useResidentInfiniteQuery,
  useSensorInfiniteQuery,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/beds/batch-allocation",
)({ component: Component });

function Component() {
  const { facilityKey } = Route.useParams();

  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useTranslation();

  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const facilityQuery = useFacilityQuery(facilityKey);
  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const sensorInfiniteQuery = useSensorInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  const beds = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .sort(function (a, b) {
            return (
              a.roomName.localeCompare(b.roomName) ||
              a.roomPronunciation.localeCompare(b.roomPronunciation) ||
              a.bedName.localeCompare(b.bedName) ||
              a.bedPronunciation.localeCompare(b.bedPronunciation) ||
              a.bedKey.localeCompare(b.bedKey)
            );
          }) ?? []
      );
    },
    [bedInfiniteQuery.data],
  );

  const facility = facilityQuery.data;

  const residents = useMemo(
    function () {
      return (
        residentInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.residents;
        }) ?? []
      );
    },
    [residentInfiniteQuery.data],
  );

  const sensors = useMemo(
    function () {
      return (
        sensorInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.sensors;
        }) ?? []
      );
    },
    [sensorInfiniteQuery.data],
  );

  const headerRows = useMemo(
    function () {
      return BedBatchAllocationHeaderRows({ t });
    },
    [t],
  );

  const formSchema = useMemo(function () {
    return v.object({ files: v.array(v.file()) });
  }, []);

  const form = useForm({
    defaultValues: useMemo(function (): v.InferInput<typeof formSchema> {
      return { files: [] };
    }, []),
    async onSubmit({ value: input }) {
      let output;
      try {
        output = parse(formSchema, input);
        console.debug("フォームが送信されました。", { input, output });
      } catch (error) {
        console.error("フォームの解析に失敗しました。", { input, output }, error);
        toast.error(t("%FormParsingFailed.0%"));
        return;
      }

      const { files } = output;

      if (files.length === 0) {
        toast.warning(t("%FileNotSelected.0%"));
        return;
      }

      const allocatedBedKeys = new Set<string>();

      for (let f = 0; f < files.length; f++) {
        const file = files[f];

        let rows;
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const characters = Encoding.codeToString(
            Encoding.convert(bytes, { from: "SJIS", to: "UNICODE" }),
          );
          rows = characters.split(/\r?\n/);
          console.info(file.name + "を読み込みました。", rows);
        } catch (error) {
          console.error(error);
          toast.error(t("%Import.1%", { $: { file } }));
          continue;
        }

        const toastId = toast.loading(
          t("%Import.10%", { $: { file } }) + " (" + 0 + " / " + rows.length + ")",
          { duration: Infinity },
        );

        let numberOfSuccessfulRows = headerRows.length;
        const failedRows = [];

        for (let r = headerRows.length; r < rows.length; r++) {
          const row = rows[r];

          toast.loading(
            t("%Import.10%", { $: { file } }) + " (" + (r + 1) + " / " + rows.length + ")",
            { id: toastId },
          );

          if (row.length === 0) {
            // 空行は無視する。
            numberOfSuccessfulRows++;
            continue;
          }

          let data;
          try {
            data = v.parse(BedBatchAllocationRowSchema(), row.split(","));
          } catch (error) {
            console.error(error);
            failedRows.push(row + "," + t("%RowParsingFailed.0%"));
            continue;
          }

          console.info("行を解析しました。", data);

          let bedKey = data.bedKey;

          // ベッドキーが指定されていない場合、居室名及びベッド名又は居室名から補完する。
          if (!bedKey && data.roomName && data.bedName) {
            bedKey = beds.find(function (bed) {
              return (
                !allocatedBedKeys.has(bed.bedKey) &&
                bed.roomName === data.roomName &&
                bed.bedName === data.bedName
              );
            })?.bedKey;

            if (bedKey) {
              allocatedBedKeys.add(bedKey);
            }
          } else if (!bedKey && data.roomName) {
            bedKey = beds.find(function (bed) {
              return !allocatedBedKeys.has(bed.bedKey) && bed.roomName === data.roomName;
            })?.bedKey;

            if (bedKey) {
              allocatedBedKeys.add(bedKey);
            }
          }

          if (!bedKey) {
            failedRows.push(row + "," + t("%BedBatchAllocation.8%"));
            continue;
          }

          let sensorKey: SensorKey | null | undefined = data.sensorKey;
          if (!sensorKey) {
            if (data.sensorType && data.sensorIdentifier) {
              switch (data.sensorType) {
                case SENSOR_TYPE.AAMS:
                  sensorKey = Array.from(sensors.values()).find(function (sensor) {
                    return (
                      sensor.type === SENSOR_TYPE.AAMS &&
                      sensor.macAddress === data.sensorIdentifier
                    );
                  })?.sensorKey;
                  break;
                case SENSOR_TYPE.ANSIEL:
                  sensorKey = Array.from(sensors.values()).find(function (sensor) {
                    return (
                      sensor.type === SENSOR_TYPE.ANSIEL &&
                      sensor.serialNumber === data.sensorIdentifier
                    );
                  })?.sensorKey;
                  break;
              }
            } else if (data.sensorName) {
              sensorKey = Array.from(sensors.values()).find(function (sensor) {
                return sensor.name === data.sensorName;
              })?.sensorKey;
            } else {
              sensorKey = null;
            }
          }

          if (sensorKey === undefined) {
            failedRows.push(row + "," + t("%BedBatchAllocation.10%"));
            continue;
          }

          let userKey: UserKey | null | undefined = data.userKey;
          if (!userKey) {
            if (data.residentName) {
              userKey = Array.from(residents.values()).find(function (resident) {
                return resident.name === data.residentName;
              })?.userKey;
            } else {
              userKey = null;
            }
          }

          // 補完が行われた上で入居者が見つからない場合、失敗とする。
          if (userKey === undefined) {
            failedRows.push(row + "," + t("%BedBatchAllocation.9%"));
            continue;
          }

          try {
            await allocateBed({ bedKey, sensorKey, userKey });
          } catch (error) {
            console.error(error);
            failedRows.push(row + "," + (error instanceof Error ? error.message : t("%Error.1%")));
            continue;
          }

          numberOfSuccessfulRows++;
        }

        if (failedRows.length > 0) {
          if (numberOfSuccessfulRows > 0) {
            toast.warning(
              t("%Import.7%", {
                $: { file, numberOfFailedRows: failedRows.length, numberOfSuccessfulRows },
              }),
              { id: toastId, duration: 5000 },
            );
          } else {
            toast.error(t("%Import.8%", { $: { file } }), { id: toastId, duration: 5000 });
          }
        } else {
          toast.success(t("%Import.6%", { $: { file } }), { id: toastId, duration: 5000 });
          await navigate({
            ignoreBlocker: true,
            params: { facilityKey },
            to: "/facilities/$facilityKey/beds/allocation",
          });
        }

        if (failedRows.length > 0) {
          saveAsFile(
            new Blob(
              [
                new Uint8Array(
                  Encoding.convert(
                    Encoding.stringToCode(headerRows.concat(failedRows).join("\r\n")),
                    { from: "UNICODE", to: "SJIS" },
                  ),
                ),
              ],
              { type: "text/csv" },
            ),
            t("%BedBatchAllocation.4%", {
              $: { facility: { facilityKey }, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
            }),
          );
        }
      }

      resetBedInfiniteQueries();
      removeResidentInfiniteQueryCache();
      removeSensorInfiniteQueryCache();
      removeSensorsCache();
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: formSchema },
  });

  const formStoreStateIsSubmitting = useSelector(form.store, function (state) {
    return state.isSubmitting;
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: t("%Form.1%"),
          no: t("%Stay.0%"),
          title: t("%Form.0%"),
          yes: t("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <form
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
      data-main
      onSubmit={function (event) {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <PageName>
        {t("%BedBatchAllocation.0%") +
          " - " +
          (facility?.name ?? (!facilityQuery.isFetching ? t("%Unknown.0%") : t("%Loading.0%")))}
      </PageName>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          maxWidth: "480px",
          padding: { smDown: "16px", sm: "16px 24px" },
          width: "stretch",
        })}
      >
        <h1
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%BedBatchAllocation.1%")}
        </h1>
        <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
          {t("%BedBatchAllocation.2%")}
        </p>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          1.&nbsp;{t("%Download.2%")}
        </h2>

        <button
          className={ButtonStyle2({ color: "gray", marginTop: "8px", width: "stretch" })}
          disabled={
            formStoreStateIsSubmitting ||
            facilityQuery.isFetching ||
            bedInfiniteQuery.isFetching ||
            residentInfiniteQuery.isFetching ||
            sensorInfiniteQuery.isFetching
          }
          onClick={function () {
            saveAsFile(
              new Blob(
                [
                  new Uint8Array(
                    Encoding.convert(
                      Encoding.stringToCode(
                        headerRows
                          .concat(
                            beds.map(function (bed) {
                              const sensor = bed.sensorKey
                                ? sensors.find(function (sensor) {
                                    return sensor.sensorKey === bed.sensorKey;
                                  })
                                : undefined;
                              const resident = bed.userKey
                                ? residents.find(function (resident) {
                                    return resident.userKey === bed.userKey;
                                  })
                                : undefined;
                              return toBedBatchAllocationRow({ bed, sensor, resident });
                            }),
                          )
                          .join("\r\n"),
                      ),
                      { from: "UNICODE", to: "SJIS" },
                    ),
                  ),
                ],
                { type: "text/csv" },
              ),
              t("%BedBatchAllocation.3%", {
                $: { facility, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
              }) + ".csv",
            );
          }}
          type="button"
        >
          <LuDownload data-icon data-left />
          {t("%Download.0%")}
        </button>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          2.&nbsp;{t("%Upload.1%")}
        </h2>

        <form.Field name="files">
          {function (field) {
            return (
              <FileUpload
                accept="text/csv"
                acceptedFiles={field.state.value}
                className={css({ marginTop: "8px" })}
                disabled={formStoreStateIsSubmitting}
                onFileChange={function ({ acceptedFiles }) {
                  field.handleChange(acceptedFiles);
                }}
              />
            );
          }}
        </form.Field>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <Link
            className={ButtonStyle2({ color: "white" })}
            disabled={formStoreStateIsSubmitting}
            onClick={function (event) {
              if (formStoreStateIsSubmitting) {
                event.preventDefault();
              } else if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds/allocation"
          >
            {t("%Cancel.0%")}
          </Link>

          <button
            className={ButtonStyle2({ color: "black" })}
            disabled={formStoreStateIsSubmitting}
            type="submit"
          >
            {formStoreStateIsSubmitting && (
              <Spinner color="#FAFAFA" data-left size="14px" type="a" />
            )}
            {t("%Import.0%")}
          </button>
        </div>
      </div>
    </form>
  );
}
